"use client";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { formatDateShort, formatTimeRange } from "@/lib/format/date";
import {
  Calendar,
  Clock,
  MapPin,
  User,
  ChevronRight,
  Ruler,
  AlertCircle,
} from "lucide-react";

export interface AppointmentCardProps {
  appointment: {
    id: string;
    status: string;
    slot?: {
      startAt?: string;
      endAt?: string;
    };
    area?: {
      name?: string;
    } | null;
    address?: {
      line1?: string;
      city?: string;
    };
    customer?: {
      publicId: string;
      name: string;
      phone?: string | null;
    };
    assignedExec?: {
      name?: string;
      publicId?: string;
    } | null;
  };
  onView?: () => void;
  onClaim?: () => void;
  onCancel?: () => void;                 // ADDED: support cancel action
  disabledActionId?: string | null;      // ADDED: disable actions for a given id (e.g., while cancelling)
  showCustomer?: boolean;
  showExec?: boolean;
  className?: string;
}

// Cover lowercase statuses; normalize via toUpperCase()
const statusConfig: Record<string, { color: string; label: string }> = {
  DRAFT: { color: "bg-gray-100 text-gray-700 border-gray-200", label: "Draft" },
  BOOKED: { color: "bg-blue-100 text-blue-700 border-blue-200", label: "Booked" },
  COMPLETED: { color: "bg-emerald-100 text-emerald-700 border-emerald-200", label: "Completed" },
  CANCELLED: { color: "bg-red-100 text-red-700 border-red-200", label: "Cancelled" },

  CONFIRMED: { color: "bg-blue-100 text-blue-700 border-blue-200", label: "Confirmed" },
  ASSIGNED: { color: "bg-cyan-100 text-cyan-700 border-cyan-200", label: "Assigned" },
  EN_ROUTE: { color: "bg-orange-100 text-orange-700 border-orange-200", label: "En Route" },
  ARRIVED: { color: "bg-teal-100 text-teal-700 border-teal-200", label: "Arrived" },
  IN_PROGRESS: { color: "bg-indigo-100 text-indigo-700 border-indigo-200", label: "In Progress" },
  MEASUREMENTS_SUBMITTED: { color: "bg-blue-100 text-blue-700 border-blue-200", label: "Submitted" },
  PENDING_CUSTOMER_ACCEPTANCE: { color: "bg-amber-100 text-amber-700 border-amber-200", label: "Pending Acceptance" },
  ACCEPTED: { color: "bg-emerald-100 text-emerald-700 border-emerald-200", label: "Accepted" },
  CORRECTION_REQUESTED: { color: "bg-orange-100 text-orange-700 border-orange-200", label: "Correction Requested" },
  CLOSED: { color: "bg-slate-100 text-slate-600 border-slate-200", label: "Closed" },
  RESCHEDULED: { color: "bg-purple-100 text-purple-700 border-purple-200", label: "Rescheduled" },
  NO_SHOW_CUSTOMER: { color: "bg-red-100 text-red-700 border-red-200", label: "No Show (Customer)" },
  NO_SHOW_EXEC: { color: "bg-red-100 text-red-700 border-red-200", label: "No Show (Exec)" },
};

export function AppointmentCard({
  appointment,
  onView,
  onClaim,
  onCancel,
  disabledActionId,
  showCustomer = false,
  showExec = true,
  className,
}: AppointmentCardProps) {
  const statusKey = (appointment.status || "DRAFT").toUpperCase();
  const status = statusConfig[statusKey] || statusConfig.DRAFT;
  const isPending = statusKey === "PENDING_CUSTOMER_ACCEPTANCE";

  const startAt = appointment.slot?.startAt || "";
  const endAt = appointment.slot?.endAt || "";

  const addressLine = appointment.address?.line1 || "—";
  const addressCity = appointment.address?.city || "";
  const areaName = appointment.area?.name || "";

  const execName = appointment.assignedExec?.name || null;

  const isBooked = statusKey === "BOOKED";
  const isCancelling = disabledActionId === appointment.id;

  return (
    <Card
      className={cn(
        "transition-shadow hover:shadow-md",
        isPending && "border-amber-300 bg-amber-50/30",
        className
      )}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0 space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1.5 text-sm font-medium">
                <Ruler className="h-4 w-4 text-muted-foreground" />
                <span>Measurement Visit</span>
              </div>
              <Badge className={cn("text-xs font-medium border", status.color)}>
                {status.label}
              </Badge>
              {isPending && (
                <Badge
                  variant="outline"
                  className="text-xs border-amber-400 text-amber-700 bg-amber-50"
                >
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Action Required
                </Badge>
              )}
            </div>

            <div className="grid gap-2 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Calendar className="h-4 w-4 shrink-0" />
                <span>{startAt ? formatDateShort(startAt) : "—"}</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Clock className="h-4 w-4 shrink-0" />
                <span>
                  {startAt && endAt ? formatTimeRange(startAt, endAt) : "—"}
                </span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <MapPin className="h-4 w-4 shrink-0" />
                <span className="truncate">
                  {addressLine}
                  {addressCity ? `, ${addressCity}` : ""}
                  {areaName ? ` (${areaName})` : ""}
                </span>
              </div>

              {showCustomer && appointment.customer && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <User className="h-4 w-4 shrink-0" />
                  <span>
                    {appointment.customer.name} ({appointment.customer.publicId})
                    {appointment.customer.phone ? ` - ${appointment.customer.phone}` : ""}
                  </span>
                </div>
              )}

              {showExec && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <User className="h-4 w-4 shrink-0" />
                  <span>{execName ? `Assigned: ${execName}` : "Not assigned"}</span>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            {onClaim && !execName && (
              <Button size="sm" onClick={onClaim}>
                Claim
              </Button>
            )}
            {onCancel && isBooked && (
              <Button variant="destructive" size="sm" onClick={onCancel} disabled={isCancelling}>
                {isCancelling ? "Cancelling..." : "Cancel"}
              </Button>
            )}
            {onView && (
              <Button variant="outline" size="sm" onClick={onView}>
                {execName ? "Open" : "View"}
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}